import { itemsToRuns } from './emphasis.js';
/**
 * Heuristic table detection.
 *
 * A "table" is recognized when within a paragraph group:
 *   - Multiple lines align on the same set of x-anchor positions (column starts)
 *   - At least 2 distinct columns
 *   - At least 2 rows
 *
 * We detect column anchors by clustering item x positions across the lines, then
 * for each line, assign each item to the nearest anchor.
 *
 * Limits: this is a "best effort" heuristic. PDF tables without grid lines are
 * frequently irregular; when in doubt the renderer prefers prose fallback.
 */
const ANCHOR_TOLERANCE_PT = 6; // items within 6pt of an anchor are bucketed to it
const MIN_ANCHOR_SEPARATION_PT = 40; // columns must be visually distinct, not just word-spacing
const MIN_COLS = 2;
const MIN_ROWS = 2;
const MAX_LINE_CHARS_FOR_TABLE = 80; // prose lines (justified body text) tend to exceed this
const MIN_GAP_BETWEEN_BLOBS_PT = 20; // a "table row" must contain real whitespace gaps, not just kerning
/** Try to detect a table from a list of lines. Returns null if no table is found. */
export function detectTable(lines) {
    if (lines.length < MIN_ROWS)
        return null;
    // Reject prose paragraphs early: if average line length is high, this isn't a table.
    const avgLineChars = lines.reduce((acc, l) => acc + l.items.reduce((a, it) => a + it.str.length, 0), 0) / lines.length;
    if (avgLineChars > MAX_LINE_CHARS_FOR_TABLE)
        return null;
    // Reject lines that are dense with text but lack large internal gaps — they're prose.
    // A real table row has visible whitespace BETWEEN cells.
    const linesWithGaps = lines.filter((line) => hasWideGap(line));
    if (linesWithGaps.length < lines.length * 0.6)
        return null;
    // Per-line item x-starts.
    const lineXStarts = lines.map((line) => line.items.map((it) => it.x));
    // Cluster all x positions into anchor groups.
    const flat = lineXStarts.flat().sort((a, b) => a - b);
    if (flat.length === 0)
        return null;
    const allAnchors = clusterAnchors(flat, ANCHOR_TOLERANCE_PT);
    // Drop anchors that are too close to each other — those are word-spacings, not columns.
    const anchors = [];
    for (const a of allAnchors) {
        const lastAccepted = anchors[anchors.length - 1];
        if (lastAccepted === undefined || a - lastAccepted >= MIN_ANCHOR_SEPARATION_PT) {
            anchors.push(a);
        }
    }
    if (anchors.length < MIN_COLS)
        return null;
    // For each line, count how many anchors it actually hits.
    let hitLines = 0;
    for (const xs of lineXStarts) {
        const hit = new Set();
        for (const x of xs) {
            const idx = nearestAnchor(x, anchors, ANCHOR_TOLERANCE_PT);
            if (idx >= 0)
                hit.add(idx);
        }
        if (hit.size >= 2)
            hitLines++;
    }
    // We need most lines to hit ≥2 anchors AND a meaningful count to call it a table.
    if (hitLines < MIN_ROWS || hitLines / lines.length < 0.6)
        return null;
    // Build the table.
    const rows = [];
    for (const line of lines) {
        // First bucket items by nearest anchor (StyledTextItem-level), then convert each bucket to InlineRun[].
        const itemBuckets = anchors.map(() => []);
        for (const item of line.items) {
            const idx = nearestAnchor(item.x, anchors, ANCHOR_TOLERANCE_PT * 2);
            if (idx >= 0)
                itemBuckets[idx].push(item);
        }
        const rowRuns = itemBuckets.map((bucket) => itemsToRuns(bucket));
        if (rowRuns.some((cell) => cell.length > 0)) {
            rows.push(rowRuns);
        }
    }
    if (rows.length < MIN_ROWS)
        return null;
    return {
        kind: 'table',
        rows,
        hasHeader: looksLikeHeader(rows),
    };
}
/** True if any two adjacent items in this line have x-gap ≥ MIN_GAP_BETWEEN_BLOBS_PT. */
function hasWideGap(line) {
    if (line.items.length < 2)
        return false;
    for (let i = 1; i < line.items.length; i++) {
        const prev = line.items[i - 1];
        const cur = line.items[i];
        if (cur.x - prev.xEnd >= MIN_GAP_BETWEEN_BLOBS_PT)
            return true;
    }
    return false;
}
function clusterAnchors(sortedX, tolerance) {
    const anchors = [];
    let cluster = [];
    for (const x of sortedX) {
        if (cluster.length === 0) {
            cluster.push(x);
            continue;
        }
        const last = cluster[cluster.length - 1];
        if (x - last <= tolerance) {
            cluster.push(x);
        }
        else {
            anchors.push(median(cluster));
            cluster = [x];
        }
    }
    if (cluster.length > 0)
        anchors.push(median(cluster));
    return anchors;
}
function median(arr) {
    const sorted = arr.slice().sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)] ?? 0;
}
function nearestAnchor(x, anchors, tolerance) {
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < anchors.length; i++) {
        const d = Math.abs(x - (anchors[i] ?? 0));
        if (d < bestDist) {
            bestDist = d;
            best = i;
        }
    }
    return bestDist <= tolerance ? best : -1;
}
function looksLikeHeader(rows) {
    if (rows.length === 0)
        return false;
    const first = rows[0];
    // Heuristic: header cells are typically all-bold or short.
    const totalRuns = first.flat();
    if (totalRuns.length === 0)
        return false;
    const boldShare = totalRuns.filter((r) => r.bold).length / totalRuns.length;
    const avgLen = totalRuns.reduce((a, r) => a + r.text.length, 0) / totalRuns.length;
    return boldShare >= 0.5 || avgLen <= 30;
}
//# sourceMappingURL=tables.js.map